import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import WalterLoader from '../shared/WalterLoader';
import { RatingBadge, ratingLabels, RATING_COLORS } from '../shared/RatingBadge.js';
import { useAppMetadata } from '../shared/useAppMetadata';
import '../../styles/ClientDashboard.css';

/* ─────────────────────────────────────────────
   Constants & config (outside component)
───────────────────────────────────────────── */

const EQUIPMENT_TYPES = [
  { key: 'faucets',  type: 'faucet',  label: 'Robinets / Lavabos', icon: '🚿', flowLabel: 'Débit',           extra: { header: 'Type de robinet', getValue: (e, td) => td[e.technical_data?.faucet_type] || '—' } },
  { key: 'wc',       type: 'wc',      label: 'WC',                  icon: '🚽', flowLabel: 'Volume de chasse', extra: null },
  { key: 'urinal',   type: 'urinal',  label: 'Urinoirs',            icon: '🚽', flowLabel: 'Volume de chasse', extra: null },
  { key: 'showers',  type: 'shower',  label: 'Douches',             icon: '🚿', flowLabel: 'Débit',           extra: null },
  { key: 'machines', type: 'machine', label: 'Machines',            icon: '⚙️', flowLabel: 'Eau utilisée',    extra: { header: 'Type de machine',  getValue: (e, td) => td[e.technical_data?.type_machine] || '—' } },
];

const TYPE_LABELS = { faucet: 'Robinet / Lavabo', wc: 'WC', urinal: 'Urinoir', shower: 'Douche', machine: 'Machine' };
const TYPE_LABELS_PLURAL = { faucet: 'robinets', wc: 'WC', urinal: 'urinoirs', shower: 'douches', machine: 'machines' };

const FIELD_CONFIG = {
  faucet_type:     { label: 'Type de robinet',       type: 'select', options: [
    { value: 'technical', label: 'Technique' }, { value: 'hand_wash', label: 'Lave-mains' },
    { value: 'changing_room', label: 'Vestiaire / Salle de bain' }, { value: 'other', label: 'Autre' },
  ]},
  type_machine:    { label: 'Type de machine',        type: 'select', options: [
    { value: 'washing_machine', label: 'Machine à laver' }, { value: 'dishwasher', label: 'Lave-vaisselle pro' },
    { value: 'dishwasher_domestic', label: 'Lave-vaisselle domestique' }, { value: 'pressure_washer', label: 'Karcher' }, { value: 'other', label: 'Autre' },
  ]},
  activation_mode: { label: "Mode d'activation",     type: 'select', options: [
    { value: 'double_handle', label: 'Double poignée' }, { value: 'single_handle', label: 'Poignée unique' },
    { value: 'infrared', label: 'Infrarouge' }, { value: 'pedal', label: 'Pédale' },
    { value: 'push_button', label: 'Bouton poussoir' }, { value: 'other', label: 'Autre' },
  ]},
  water_quality_in:{ label: "Qualité de l'eau",       type: 'select', options: [
    { value: 'drinkable', label: 'Potable' }, { value: 'non_drinkable', label: 'Non potable' },
  ]},
  nb_equipment:    { label: "Nombre d'équipements",   type: 'number' },
  max_flow:        { label: 'Débit maximal',          type: 'number', unitField: 'max_flow_unit' },
  max_flow_unit:   { hidden: true },
  flush_volume:    { label: 'Volume de chasse (L)',   type: 'number' },
  flush_volume_max:{ label: 'Volume max chasse (L)',  type: 'number' },
  flush_volume_min:{ label: 'Volume min chasse (L)',  type: 'number' },
  volume_per_cycle:{ label: 'Volume / débit',         type: 'number', unitField: 'volume_per_cycle_unit' },
  volume_per_cycle_unit: { hidden: true },
  timer_duration:  { label: 'Durée minuteur (min)',   type: 'number' },
  foamer:          { label: 'Mousseur',               type: 'boolean' },
  hot_water:       { label: 'Raccordé eau chaude / eau chauffée', type: 'boolean' },
  hot_water_ratio: { label: "Part d'eau chaude typique (%)", type: 'number' },
  isolation_valve: { label: "Vanne d'isolement",      type: 'boolean' },
  timer:           { label: 'Minuteur',               type: 'boolean' },
  dual_flush:      { label: 'Double chasse',          type: 'boolean' },
  built_in:        { label: 'Encastré',               type: 'boolean' },
  is_water_tank:   { label: "Réservoir d'eau",        type: 'boolean' },
  reference:       { label: 'Référence',              type: 'text' },
  general_comment: { label: 'Commentaire',            type: 'textarea' },
};

const translationDict = {
  "washing_machine": "Machine à laver", "dishwasher": "Lave-vaisselle", "other": "Autre",
  "pressure_washer": "Karcher", "dishwasher_domestic": "Lave-vaisselle domestique",
  "changing_room": "Vestiaire / Salle de bain", "hand_wash": "Lave-mains", "technical": "Technique",
};

/* ─────────────────────────────────────────────
   Pure helper functions
───────────────────────────────────────────── */

// Types de machine : référentiel app_metadata (écran /admin/machine-types),
// la liste statique de FIELD_CONFIG ne sert que tant qu'il n'est pas chargé.
const fieldOptions = (key, machineTypes) =>
  key === 'type_machine' && machineTypes?.length ? machineTypes : FIELD_CONFIG[key]?.options;

const formatFieldValue = (key, value, machineTypes) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  const cfg = FIELD_CONFIG[key];
  if (cfg?.type === 'select') return fieldOptions(key, machineTypes)?.find(o => o.value === String(value))?.label || String(value);
  return String(value);
};

const getFieldValueCounts = (key, techDataList) => {
  const values = techDataList.map(td => td[key]);
  const countMap = values.reduce((acc, v) => {
    const k = String(v);
    if (!acc[k]) acc[k] = { value: v, count: 0 };
    acc[k].count++;
    return acc;
  }, {});
  return Object.values(countMap);
};

/* ─────────────────────────────────────────────
   Sub-components
───────────────────────────────────────────── */

const ChevronIcon = ({ open }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"
    style={{ flexShrink: 0, transition: 'transform 250ms', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', color: 'var(--color-on-surface-var)' }}>
    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Checkbox = ({ checked, onChange, size = 18 }) => (
  <span
    className={`ceq-checkbox ${checked ? 'ceq-checkbox--checked' : ''}`}
    style={{ width: size, height: size }}
    onClick={e => { e.stopPropagation(); onChange(!checked); }}
    role="checkbox"
    aria-checked={checked}
    tabIndex={0}
    onKeyDown={e => e.key === ' ' && onChange(!checked)}
  >
    {checked && (
      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
        <path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )}
  </span>
);

const ToggleField = ({ value, onChange, disabled }) => (
  <div className={`ceq-toggle-row ${disabled ? 'ceq-toggle-row--disabled' : ''}`}>
    <button type="button"
      className={`ceq-toggle-track ${value ? 'ceq-toggle-track--on' : 'ceq-toggle-track--off'}`}
      onClick={() => !disabled && onChange(!value)} disabled={disabled}>
      <span className="ceq-toggle-thumb" />
    </button>
    <span className="ceq-toggle-value">{value ? 'Oui' : 'Non'}</span>
  </div>
);

/* ─────────────────────────────────────────────
   Field input renderer (shared by detail + bulk modals)
───────────────────────────────────────────── */

const FieldInput = ({ fieldKey, value, onChange, disabled, editData, machineTypes }) => {
  const cfg = FIELD_CONFIG[fieldKey];
  if (!cfg) return null;

  if (cfg.type === 'boolean') {
    return <ToggleField value={!!value} onChange={onChange} disabled={disabled} />;
  }
  if (cfg.type === 'number') {
    const unit = cfg.unitField ? (editData?.[cfg.unitField]) : undefined;
    return unit ? (
      <div className={`ceq-field-with-unit ${disabled ? 'ceq-field--disabled' : ''}`}>
        <input type="number" className="ceq-field-input" value={value ?? ''} disabled={disabled}
          onChange={e => onChange(parseFloat(e.target.value))} />
        <span className="ceq-field-unit">{unit}</span>
      </div>
    ) : (
      <input type="number" className="ceq-field-input" value={value ?? ''} disabled={disabled}
        onChange={e => onChange(parseFloat(e.target.value))} />
    );
  }
  if (cfg.type === 'select') {
    return (
      <select className="ceq-field-select" value={value ?? ''} disabled={disabled}
        onChange={e => onChange(e.target.value)}>
        {fieldOptions(fieldKey, machineTypes).map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </select>
    );
  }
  if (cfg.type === 'textarea') {
    return (
      <textarea className="ceq-field-textarea" value={value ?? ''} disabled={disabled} rows={3}
        onChange={e => onChange(e.target.value)} />
    );
  }
  return (
    <input type="text" className="ceq-field-input" value={value ?? ''} disabled={disabled}
      onChange={e => onChange(e.target.value)} />
  );
};

/* ═════════════════════════════════════════════
   Main component
═════════════════════════════════════════════ */

const ClientEquipments = () => {
  const navigate = useNavigate();
  const { building_id } = useParams();
  const { data: machineTypes } = useAppMetadata('machine_types');
  const typeLabels = useMemo(() => ({
    ...translationDict,
    ...Object.fromEntries(machineTypes.map(t => [t.value, t.label])),
  }), [machineTypes]);

  // ── Data state ──
  const [dataEquipments, setDataEquipments]           = useState([]);
  const [loading, setLoading]                         = useState(true);
  const [error, setError]                             = useState(null);

  // ── Filter state ──
  const [selectedSections, setSelectedSections]       = useState([]);
  const [selectedRooms, setSelectedRooms]             = useState([]);
  const [selectedPerformance, setSelectedPerformance] = useState([]);
  const [showAllRooms, setShowAllRooms]               = useState(false);
  const ROOMS_VISIBLE = 6;

  // ── Accordion state ──
  const [openSections, setOpenSections] = useState({ faucets: true, wc: true, urinal: true, machines: true, showers: true });

  // ── Detail modal state ──
  const [selectedEquipment, setSelectedEquipment]     = useState(null);
  const [editData, setEditData]                       = useState({});
  const [saving, setSaving]                           = useState(false);
  const [saveMessage, setSaveMessage]                 = useState(null);

  // ── Bulk mode state ──
  const [bulkMode, setBulkMode]                       = useState(false);
  const [selectedIds, setSelectedIds]                 = useState(new Set());
  const [bulkModalOpen, setBulkModalOpen]             = useState(false);
  const [bulkChanges, setBulkChanges]                 = useState({});
  const [fieldsToUpdate, setFieldsToUpdate]           = useState(new Set());
  const [bulkSaving, setBulkSaving]                   = useState(false);
  const [bulkMessage, setBulkMessage]                 = useState(null);

  /* ── Fetch ── */
  useEffect(() => {
    if (!building_id) return;
    fetch(`${process.env.REACT_APP_API_URL}/load_equipments_with_rating/${building_id}`)
      .then(r => { if (!r.ok) throw new Error("Erreur lors de la récupération des équipements."); return r.json(); })
      .then(data => { setDataEquipments(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [building_id]);

  /* ── Detail modal init ── */
  useEffect(() => {
    if (selectedEquipment) { setEditData({ ...selectedEquipment.technical_data }); setSaveMessage(null); }
  }, [selectedEquipment]);

  /* ── Filter derived data ── */
  const allSections = useMemo(() =>
    dataEquipments.map(e => ({ id: e.location.section_id, name: e.location.name_section || e.location.section_id }))
      .filter((v, i, self) => self.findIndex(s => s.id === v.id) === i),
    [dataEquipments]
  );

  const availableRooms = useMemo(() =>
    dataEquipments
      .filter(e => selectedSections.length === 0 || selectedSections.includes(e.location.section_id))
      .map(e => ({ id: e.location.room_id, name: e.location.name_room || e.location.room_id }))
      .filter((v, i, self) => self.findIndex(r => r.id === v.id) === i),
    [dataEquipments, selectedSections]
  );

  useEffect(() => {
    setShowAllRooms(false);
    if (selectedSections.length === 0) return;
    const validIds = new Set(availableRooms.map(r => r.id));
    setSelectedRooms(prev => prev.filter(id => validIds.has(id)));
  }, [selectedSections, availableRooms]);

  const filteredEquipments = useMemo(() =>
    dataEquipments.filter(e =>
      (selectedSections.length === 0 || selectedSections.includes(e.location.section_id)) &&
      (selectedRooms.length === 0     || selectedRooms.includes(e.location.room_id)) &&
      (selectedPerformance.length === 0 || selectedPerformance.includes(e.rating_letter))
    ),
    [dataEquipments, selectedSections, selectedRooms, selectedPerformance]
  );

  /* ── Bulk analysis ── */
  const selectedItems = useMemo(() =>
    dataEquipments.filter(e => selectedIds.has(e._id)),
    [dataEquipments, selectedIds]
  );

  const bulkAnalysis = useMemo(() => {
    if (selectedItems.length === 0) return { commonKeys: [], fieldSummary: {} };
    const tdList = selectedItems.map(e => e.technical_data || {});
    const commonKeys = Object.keys(FIELD_CONFIG).filter(key =>
      !FIELD_CONFIG[key].hidden && tdList.every(td => key in td)
    );
    const fieldSummary = {};
    commonKeys.forEach(key => {
      const valueCounts = getFieldValueCounts(key, tdList);
      fieldSummary[key] = {
        allSame: valueCounts.length === 1,
        currentValue: valueCounts.length === 1 ? valueCounts[0].value : null,
        valueCounts,
      };
    });
    return { commonKeys, fieldSummary };
  }, [selectedItems]);

  const selectionBreakdown = useMemo(() => {
    const byType = {};
    selectedItems.forEach(e => { byType[e.type_equipment] = (byType[e.type_equipment] || 0) + 1; });
    return byType;
  }, [selectedItems]);

  /* ── Handlers ── */
  const toggleChip    = (setter, value) => setter(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
  const toggleSection = key => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  const resetFilters  = () => { setSelectedSections([]); setSelectedRooms([]); setSelectedPerformance([]); };

  const closeDetailModal = () => { setSelectedEquipment(null); setEditData({}); setSaveMessage(null); };

  const toggleItemSelect = useCallback((id, e) => {
    e?.stopPropagation();
    setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);

  const selectAllOfType = useCallback((type) => {
    const items = filteredEquipments.filter(e => e.type_equipment === type);
    const allSelected = items.every(e => selectedIds.has(e._id));
    setSelectedIds(prev => {
      const n = new Set(prev);
      items.forEach(e => allSelected ? n.delete(e._id) : n.add(e._id));
      return n;
    });
  }, [filteredEquipments, selectedIds]);

  const exitBulkMode = () => { setBulkMode(false); setSelectedIds(new Set()); };

  const openBulkModal = () => {
    setBulkChanges({});
    setFieldsToUpdate(new Set());
    setBulkMessage(null);
    setBulkModalOpen(true);
  };

  const toggleFieldToUpdate = (key) => {
    if (!fieldsToUpdate.has(key)) {
      const summary = bulkAnalysis.fieldSummary[key];
      if (summary) {
        const initVal = summary.allSame
          ? summary.currentValue
          : summary.valueCounts.sort((a, b) => b.count - a.count)[0]?.value;
        setBulkChanges(prev => ({ ...prev, [key]: initVal }));
      }
    }
    setFieldsToUpdate(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  };

  /* ── API calls ── */
  const saveEquipment = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/update_equipment/${selectedEquipment._id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ technical_data: editData }),
      });
      if (!res.ok) throw new Error('Erreur lors de la sauvegarde');
      setDataEquipments(prev => prev.map(e => e._id === selectedEquipment._id ? { ...e, technical_data: editData } : e));
      setSaveMessage({ type: 'success', text: 'Équipement mis à jour avec succès' });
    } catch (err) {
      setSaveMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const saveBulkChanges = async () => {
    if (fieldsToUpdate.size === 0) return;
    setBulkSaving(true);
    const payload = {};
    fieldsToUpdate.forEach(key => { payload[key] = bulkChanges[key]; });
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/update_equipments_bulk`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: [...selectedIds], technical_data: payload }),
      });
      if (!res.ok) throw new Error('Erreur lors de la sauvegarde groupée');
      setDataEquipments(prev =>
        prev.map(e => selectedIds.has(e._id) ? { ...e, technical_data: { ...e.technical_data, ...payload } } : e)
      );
      setBulkMessage({ type: 'success', text: `${selectedIds.size} équipement(s) mis à jour` });
      setTimeout(() => { setBulkModalOpen(false); exitBulkMode(); }, 1600);
    } catch (err) {
      setBulkMessage({ type: 'error', text: err.message });
    } finally {
      setBulkSaving(false);
    }
  };

  /* ── Derived display ── */
  const hasActiveFilters = selectedSections.length > 0 || selectedRooms.length > 0 || selectedPerformance.length > 0;
  const activeCount = selectedSections.length + selectedRooms.length + selectedPerformance.length;
  const clientId = dataEquipments[0]?.location?.client_id;

  /* ── Detail modal field renderer ── */
  const renderDetailFields = () => (
    Object.keys(FIELD_CONFIG)
      .filter(k => !FIELD_CONFIG[k].hidden && k in editData)
      .map(key => {
        const cfg = FIELD_CONFIG[key];
        const isFullWidth = cfg.type === 'textarea';
        return (
          <div key={key} className={`ceq-field-group ${isFullWidth ? 'ceq-field-group--full' : ''}`}>
            <label className="ceq-field-label">{cfg.label}</label>
            <FieldInput
              fieldKey={key} value={editData[key]} editData={editData} machineTypes={machineTypes}
              onChange={v => setEditData(p => ({ ...p, [key]: v }))}
            />
          </div>
        );
      })
  );

  /* ── Bulk modal field renderer ── */
  const renderBulkFields = () => {
    const { commonKeys, fieldSummary } = bulkAnalysis;
    if (commonKeys.length === 0) return (
      <p style={{ color: 'var(--color-on-surface-var)', fontSize: '0.875rem', textAlign: 'center', padding: '20px 0' }}>
        Aucun champ commun trouvé entre les équipements sélectionnés.
      </p>
    );

    return commonKeys.map(key => {
      const cfg     = FIELD_CONFIG[key];
      const summary = fieldSummary[key];
      const enabled = fieldsToUpdate.has(key);

      // First unit-bearing field needs its unit from one of the items
      const sampleTd = selectedItems[0]?.technical_data || {};

      return (
        <div key={key} className={`ceq-bulk-field ${enabled ? 'ceq-bulk-field--active' : ''}`}>
          <div className="ceq-bulk-field-header">
            <span className="ceq-field-label">{cfg.label}</span>
            <label className="ceq-bulk-enable" onClick={() => toggleFieldToUpdate(key)}>
              <Checkbox checked={enabled} onChange={() => toggleFieldToUpdate(key)} size={16} />
              <span>Modifier</span>
            </label>
          </div>

          {/* Current values summary */}
          <div className={`ceq-bulk-summary ${summary.allSame ? 'ceq-bulk-summary--same' : 'ceq-bulk-summary--diff'}`}>
            {summary.allSame ? (
              <>
                <span className="ceq-bulk-summary-dot ceq-bulk-dot--same" />
                <span>Identiques :&nbsp;<strong>{formatFieldValue(key, summary.currentValue, machineTypes)}</strong></span>
              </>
            ) : (
              <>
                <span className="ceq-bulk-summary-dot ceq-bulk-dot--diff" />
                <span>{summary.valueCounts.length} valeurs :&nbsp;</span>
                {summary.valueCounts.sort((a, b) => b.count - a.count).map(({ value, count }) => (
                  <span key={String(value)} className="ceq-bulk-value-tag">
                    {formatFieldValue(key, value, machineTypes)} ×{count}
                  </span>
                ))}
              </>
            )}
          </div>

          {/* Edit input (enabled only when checkbox is checked) */}
          <div className={`ceq-bulk-field-input ${!enabled ? 'ceq-bulk-field-input--off' : ''}`}>
            <FieldInput
              fieldKey={key}
              value={enabled ? bulkChanges[key] : (summary.allSame ? summary.currentValue : null)}
              editData={sampleTd}
              machineTypes={machineTypes}
              onChange={v => setBulkChanges(p => ({ ...p, [key]: v }))}
              disabled={!enabled}
            />
          </div>
        </div>
      );
    });
  };

  /* ── Guards ── */
  if (loading) return <div className="centered-spinner-dashboard"><WalterLoader /></div>;
  if (error)   return <p>Erreur : {error}</p>;

  /* ══════════════════════════════════════════════ */
  return (
    <div className={`ceq-page ${bulkMode ? 'ceq-page--bulk' : ''}`}>

      {/* ── Page header ── */}
      <div className="ceq-header">
        <button type="button" className="back-button" onClick={() => navigate(`/client/${clientId}/${building_id}`)}>
          ← Tableau de bord
        </button>
        <h1 className="ceq-title">Liste des équipements</h1>
        <div className="ceq-header-actions">
          {bulkMode ? (
            <button type="button" className="ceq-bulk-mode-btn ceq-bulk-mode-btn--exit" onClick={exitBulkMode}>
              ✕ Quitter la sélection
            </button>
          ) : (
            <button type="button" className="ceq-bulk-mode-btn" onClick={() => setBulkMode(true)}>
              ☑ Modifier en bloc
            </button>
          )}
        </div>
      </div>

      {/* ── Filter panel ── */}
      <div className="ceq-filter-card">
        <div className="ceq-filter-header">
          <div className="ceq-filter-section-title">
            <span className="ceq-filter-accent-bar" />
            <span className="ceq-filter-title-text">Filtres</span>
            {hasActiveFilters && <span className="ceq-active-badge">{activeCount} actif{activeCount > 1 ? 's' : ''}</span>}
          </div>
          {hasActiveFilters && <button type="button" className="ceq-reset-btn" onClick={resetFilters}>Réinitialiser</button>}
        </div>

        <div className="ceq-filter-group">
          <span className="ceq-filter-label">Section</span>
          <div className="ceq-filter-chips">
            {allSections.map(s => (
              <button key={s.id} type="button"
                className={`ceq-chip${selectedSections.includes(s.id) ? ' ceq-chip--active' : ''}`}
                onClick={() => toggleChip(setSelectedSections, s.id)}>{s.name}</button>
            ))}
          </div>
        </div>

        <div className="ceq-filter-group">
          <span className="ceq-filter-label">
            Salle
            {selectedSections.length > 0 && (
              <span className="ceq-filter-label-hint"> — {selectedSections.length === 1 ? '1 section sélectionnée' : `${selectedSections.length} sections sélectionnées`}</span>
            )}
          </span>
          <div className="ceq-filter-chips">
            {(showAllRooms ? availableRooms : availableRooms.slice(0, ROOMS_VISIBLE)).map(r => (
              <button key={r.id} type="button"
                className={`ceq-chip${selectedRooms.includes(r.id) ? ' ceq-chip--active' : ''}`}
                onClick={() => toggleChip(setSelectedRooms, r.id)}>{r.name}</button>
            ))}
            {!showAllRooms && availableRooms.length > ROOMS_VISIBLE && (
              <button type="button" className="ceq-chip ceq-chip--expand" onClick={() => setShowAllRooms(true)}>
                + {availableRooms.length - ROOMS_VISIBLE} salles
              </button>
            )}
            {showAllRooms && availableRooms.length > ROOMS_VISIBLE && (
              <button type="button" className="ceq-chip ceq-chip--expand" onClick={() => setShowAllRooms(false)}>Réduire</button>
            )}
          </div>
        </div>

        <div className="ceq-filter-group">
          <span className="ceq-filter-label">Note de performance</span>
          <div className="ceq-filter-chips">
            {Object.entries(ratingLabels).map(([letter, label]) => {
              const c = RATING_COLORS[letter] || {};
              const isActive = selectedPerformance.includes(letter);
              return (
                <button key={letter} type="button" className="ceq-perf-chip"
                  style={isActive ? { background: c.tintBg, color: c.tintText, borderColor: c.border, fontWeight: 600 } : {}}
                  onClick={() => toggleChip(setSelectedPerformance, letter)}>
                  <span className="ceq-perf-dot" style={{ background: c.bg }} />
                  <span className="ceq-perf-letter">{letter}</span>
                  <span className="ceq-perf-label-text">{label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Equipment type accordions ── */}
      {EQUIPMENT_TYPES.map(({ key, type, label, icon, flowLabel, extra }) => {
        const items  = filteredEquipments.filter(e => e.type_equipment === type);
        if (items.length === 0) return null;
        const isOpen = openSections[key];
        const allTypeSelected = items.every(e => selectedIds.has(e._id));
        const someTypeSelected = items.some(e => selectedIds.has(e._id));

        return (
          <div key={key} className="ceq-accordion">
            <button type="button" className="ceq-accordion-header" onClick={() => toggleSection(key)} aria-expanded={isOpen}>
              <div className="ceq-accordion-left">
                <span className="ceq-accordion-icon" role="img" aria-hidden="true">{icon}</span>
                <span className="ceq-accordion-title">{label}</span>
                <span className="ceq-count-chip">{items.length} équipement{items.length > 1 ? 's' : ''}</span>
                {bulkMode && (
                  <button type="button" className="ceq-select-all-btn"
                    onClick={e => { e.stopPropagation(); selectAllOfType(type); }}>
                    {allTypeSelected ? 'Désélectionner tout' : someTypeSelected ? 'Compléter la sélection' : 'Tout sélectionner'}
                  </button>
                )}
              </div>
              <ChevronIcon open={isOpen} />
            </button>

            {isOpen && (
              <div className="ceq-accordion-body">
                <div className="ceq-table-wrapper">
                  <table className="ceq-table">
                    <thead>
                      <tr>
                        {bulkMode && <th className="ceq-table-th ceq-th-check" />}
                        <th className="ceq-table-th ceq-th-center" style={{ width: 56 }}>Note</th>
                        {extra && <th className="ceq-table-th">{extra.header}</th>}
                        <th className="ceq-table-th">Section</th>
                        <th className="ceq-table-th">Salle</th>
                        <th className="ceq-table-th">{flowLabel}</th>
                        <th className="ceq-table-th ceq-th-right">Consommation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(equipment => {
                        const isSelected = selectedIds.has(equipment._id);
                        return (
                          <tr key={equipment._id}
                            className={`ceq-table-row ${isSelected ? 'ceq-table-row--selected' : ''}`}
                            onClick={e => bulkMode ? toggleItemSelect(equipment._id, e) : setSelectedEquipment(equipment)}>
                            {bulkMode && (
                              <td className="ceq-table-td ceq-td-check">
                                <Checkbox checked={isSelected} onChange={() => toggleItemSelect(equipment._id)} />
                              </td>
                            )}
                            <td className="ceq-table-td ceq-td-center"><RatingBadge letter={equipment.rating_letter} /></td>
                            {extra && <td className="ceq-table-td">{extra.getValue(equipment, typeLabels)}</td>}
                            <td className="ceq-table-td ceq-td-secondary">{equipment.location.name_section || equipment.location.section_id}</td>
                            <td className="ceq-table-td ceq-td-secondary">{equipment.location.name_room || equipment.location.room_id}</td>
                            <td className="ceq-table-td ceq-td-secondary">{equipment.water_value} {equipment.water_value_unit}</td>
                            <td className="ceq-table-td ceq-td-numeric">{Number(equipment.total_real_consumption).toFixed(1)} m³</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* ── Bulk selection footer ── */}
      {bulkMode && selectedIds.size > 0 && (
        <div className="ceq-bulk-footer">
          <div className="ceq-bulk-footer-left">
            <div className="ceq-bulk-footer-count">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                <rect x="1" y="1" width="14" height="14" rx="3" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.4)" strokeWidth="1.2"/>
                <path d="M4 8l3 3 5-6" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span><strong>{selectedIds.size}</strong> équipement{selectedIds.size > 1 ? 's' : ''} sélectionné{selectedIds.size > 1 ? 's' : ''}</span>
            </div>
            <div className="ceq-bulk-footer-breakdown">
              {Object.entries(selectionBreakdown).map(([type, count]) => (
                <span key={type} className="ceq-bulk-breakdown-tag">
                  {count} {TYPE_LABELS_PLURAL[type] || type}
                </span>
              ))}
            </div>
          </div>
          <div className="ceq-bulk-footer-actions">
            <button type="button" className="ceq-bulk-footer-clear" onClick={() => setSelectedIds(new Set())}>
              Tout désélectionner
            </button>
            <button type="button" className="ceq-bulk-footer-cta" onClick={openBulkModal}>
              Modifier en bloc →
            </button>
          </div>
        </div>
      )}

      {/* ── Detail modal ── */}
      {selectedEquipment && (
        <>
          <div className="ceq-modal-backdrop" onClick={closeDetailModal} />
          <div className="ceq-modal" role="dialog" aria-modal="true">
            <div className="ceq-modal-header">
              <div>
                <p className="ceq-modal-header-label">{TYPE_LABELS[selectedEquipment.type_equipment] || 'Équipement'}</p>
                <h2 className="ceq-modal-header-title">
                  {selectedEquipment.location.name_section}
                  <span className="ceq-modal-breadcrumb-sep"> › </span>
                  {selectedEquipment.location.name_room}
                </h2>
              </div>
              <button type="button" className="ceq-modal-close" onClick={closeDetailModal} aria-label="Fermer">✕</button>
            </div>

            <div className="ceq-modal-body">
              <div className="ceq-modal-visual">
                <div className="ceq-modal-photo">
                  {selectedEquipment.image_url
                    ? <img src={selectedEquipment.image_url} alt="Équipement" className="ceq-modal-photo-img" />
                    : <div className="ceq-modal-photo-placeholder">
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
                          <polyline points="21 15 16 10 5 21"/>
                        </svg>
                      </div>
                  }
                </div>
                <div className="ceq-modal-metrics">
                  <div className="ceq-modal-metric-row">
                    <RatingBadge letter={selectedEquipment.rating_letter} large />
                    <div>
                      <p className="ceq-modal-metric-label">Note de performance</p>
                      <p className="ceq-modal-metric-value">{ratingLabels[selectedEquipment.rating_letter] || '—'}</p>
                    </div>
                  </div>
                  <div className="ceq-modal-metric-grid">
                    <div>
                      <p className="ceq-modal-metric-label">Consommation annuelle</p>
                      <p className="ceq-modal-metric-big">{Number(selectedEquipment.total_real_consumption).toFixed(1)} <span className="ceq-modal-metric-unit">m³</span></p>
                    </div>
                    <div>
                      <p className="ceq-modal-metric-label">Note terrain</p>
                      <p className="ceq-modal-metric-big">{selectedEquipment.rate ? `${Math.round(selectedEquipment.rate * 100)}` : '—'}<span className="ceq-modal-metric-unit">{selectedEquipment.rate ? ' %' : ''}</span></p>
                    </div>
                    <div>
                      <p className="ceq-modal-metric-label">{selectedEquipment.water_value_unit?.includes('min') ? 'Débit' : 'Volume'}</p>
                      <p className="ceq-modal-metric-big">{selectedEquipment.water_value ?? '—'} <span className="ceq-modal-metric-unit">{selectedEquipment.water_value_unit || ''}</span></p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="ceq-modal-section-title">
                <span className="ceq-filter-accent-bar" />
                <span>Données techniques</span>
              </div>

              <div className="ceq-modal-fields">{renderDetailFields()}</div>

              {saveMessage && (
                <div className={`ceq-modal-msg ceq-modal-msg--${saveMessage.type}`}>{saveMessage.text}</div>
              )}
            </div>

            <div className="ceq-modal-footer">
              <button type="button" className="ceq-modal-cancel" onClick={closeDetailModal}>Annuler</button>
              <button type="button" className="ceq-modal-save" onClick={saveEquipment} disabled={saving}>
                {saving ? 'Enregistrement…' : 'Enregistrer →'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Bulk edit modal ── */}
      {bulkModalOpen && (
        <>
          <div className="ceq-modal-backdrop" onClick={() => !bulkSaving && setBulkModalOpen(false)} />
          <div className="ceq-modal ceq-modal--bulk" role="dialog" aria-modal="true">
            <div className="ceq-modal-header">
              <div>
                <p className="ceq-modal-header-label">Modification en bloc</p>
                <h2 className="ceq-modal-header-title">{selectedIds.size} équipement{selectedIds.size > 1 ? 's' : ''} sélectionné{selectedIds.size > 1 ? 's' : ''}</h2>
              </div>
              <button type="button" className="ceq-modal-close" onClick={() => !bulkSaving && setBulkModalOpen(false)}>✕</button>
            </div>

            <div className="ceq-modal-body">
              {/* Info banner */}
              <div className="ceq-bulk-info-banner">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                  <circle cx="8" cy="8" r="7" stroke="#0097d6" strokeWidth="1.4"/>
                  <path d="M8 7v4" stroke="#0097d6" strokeWidth="1.4" strokeLinecap="round"/>
                  <circle cx="8" cy="5" r="0.75" fill="#0097d6"/>
                </svg>
                <p>Seuls les <strong>champs communs</strong> à tous les équipements sélectionnés sont affichés. Cochez un champ pour le modifier, les autres resteront inchangés.</p>
              </div>

              {/* Common fields */}
              <div className="ceq-bulk-fields">{renderBulkFields()}</div>

              {/* Warning */}
              {fieldsToUpdate.size > 0 && (
                <div className="ceq-bulk-warning">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                    <path d="M8 2L14.5 13.5H1.5L8 2Z" stroke="#964900" strokeWidth="1.4" strokeLinejoin="round"/>
                    <path d="M8 6.5V9" stroke="#964900" strokeWidth="1.4" strokeLinecap="round"/>
                    <circle cx="8" cy="11" r="0.75" fill="#964900"/>
                  </svg>
                  <p>
                    <strong>{fieldsToUpdate.size} champ{fieldsToUpdate.size > 1 ? 's' : ''}</strong> seront modifiés sur <strong>{selectedIds.size} équipement{selectedIds.size > 1 ? 's' : ''}</strong>. Cette action est immédiate et remplacera les valeurs existantes.
                  </p>
                </div>
              )}

              {bulkMessage && (
                <div className={`ceq-modal-msg ceq-modal-msg--${bulkMessage.type}`}>{bulkMessage.text}</div>
              )}
            </div>

            <div className="ceq-modal-footer">
              <button type="button" className="ceq-modal-cancel" onClick={() => !bulkSaving && setBulkModalOpen(false)}>
                Annuler
              </button>
              <button type="button" className="ceq-modal-save" onClick={saveBulkChanges}
                disabled={bulkSaving || fieldsToUpdate.size === 0}>
                {bulkSaving ? 'Enregistrement…' : `Appliquer à ${selectedIds.size} équipement${selectedIds.size > 1 ? 's' : ''} →`}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ClientEquipments;
